/**
 * Integrations page - Comprehensive redesign
 */

import React, {useState, useMemo} from 'react';
import {Link} from 'react-router-dom';
import {LandingLayout} from '../../components/layout/LandingLayout';
import {Container} from '../../components/ui/Container';
import {Section} from '../../components/ui/Section';
import {Card} from '../../components/ui/Card';
import {Button} from '../../components/ui/Button';
import {categories, integrations, type Integration, type IntegrationCategory} from '../../config/integrations';
import {ROUTES} from '../../config/constants';
import {useScrollAnimation} from '../../hooks/useScrollAnimation';
import {cn} from '../../utils/cn';

export function IntegrationsPage() {
  const [selectedCategory, setSelectedCategory] = useState<IntegrationCategory | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const {ref: heroRef, isVisible: heroVisible} = useScrollAnimation();
  const {ref: categoriesRef, isVisible: categoriesVisible} = useScrollAnimation();
  const {ref: ctaRef, isVisible: ctaVisible} = useScrollAnimation();

  // Filter integrations based on category and search
  const filteredIntegrations = useMemo(() => {
    let filtered = integrations;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((integration) => integration.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (integration) =>
          integration.name.toLowerCase().includes(query) ||
          integration.description?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [selectedCategory, searchQuery]);

  // Group filtered integrations by category
  const integrationsByCategory = useMemo(() => {
    const grouped: Record<string, Integration[]> = {};
    
    filteredIntegrations.forEach((integration) => {
      const categoryLabel = categories.find((c) => c.id === integration.category)?.label || 'Other';
      if (!grouped[categoryLabel]) {
        grouped[categoryLabel] = [];
      }
      grouped[categoryLabel].push(integration);
    });

    return Object.entries(grouped).map(([category, items]) => ({
      category,
      items,
    }));
  }, [filteredIntegrations]);

  return (
    <LandingLayout>
      {/* Hero Section */}
      <Section padding="lg" background="white" className="pt-32 pb-20 md:pb-24 bg-background dark:bg-[#1E1E1E]">
        <Container>
          <div
            ref={heroRef as React.RefObject<HTMLDivElement>}
            className={`max-w-4xl mx-auto text-center transition-all duration-700 ${
              heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-textPrimary dark:text-[#CCCCCC] mb-6 leading-tight">
              Integrations
            </h1>
            <p className="text-xl sm:text-2xl text-textSecondary dark:text-[#858585] max-w-3xl mx-auto leading-relaxed mb-8">
              Connect VoxaPilot with your favorite tools. Seamlessly integrate with meeting platforms, productivity apps, and business tools.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search integrations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={cn(
                    'w-full px-6 py-4 pl-12 rounded-lg border',
                    'bg-white dark:bg-[#1E1E1E] text-textPrimary dark:text-[#CCCCCC]',
                    'placeholder:text-gray-400 dark:placeholder:text-[#6A6A6A]',
                    'focus:outline-none focus:ring-2 focus:ring-orange-600 dark:focus:ring-orange-500 focus:border-transparent',
                    'border-gray-200 dark:border-gray-700',
                    'transition-colors'
                  )}
                />
                <svg
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-textTertiary dark:text-[#6A6A6A]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap justify-center gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={cn(
                  'px-4 py-2 rounded-md text-sm font-medium transition-all',
                  selectedCategory === 'all'
                    ? 'bg-[#1E1E1E] dark:bg-gray-700 text-white'
                    : 'bg-white dark:bg-[#252526] text-textSecondary dark:text-[#858585] hover:bg-gray-50 dark:hover:bg-[#2D2D30] border border-gray-200 dark:border-gray-700'
                )}
              >
                All
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={cn(
                    'px-4 py-2 rounded-md text-sm font-medium transition-all',
                    selectedCategory === category.id
                      ? 'bg-[#1E1E1E] dark:bg-gray-700 text-white'
                      : 'bg-white dark:bg-[#252526] text-textSecondary dark:text-[#858585] hover:bg-gray-50 dark:hover:bg-[#2D2D30] border border-gray-200 dark:border-gray-700'
                  )}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* All Integrations by Category */}
      <Section padding="lg" background="white" ref={categoriesRef as React.RefObject<HTMLElement>} className="bg-background dark:bg-[#1E1E1E]">
        <Container>
          <div className={`transition-all duration-700 ${
            categoriesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            {filteredIntegrations.length > 0 ? (
              <>
                {integrationsByCategory.length > 0 ? (
                  integrationsByCategory.map((categoryGroup, categoryIndex) => (
                    <div key={categoryGroup.category} className="mb-16 last:mb-0">
                      <h2 
                        className={`text-2xl sm:text-3xl font-semibold text-textPrimary dark:text-[#CCCCCC] mb-8 transition-all duration-700 ${
                          categoriesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                        }`}
                        style={{transitionDelay: `${categoryIndex * 0.1}s`}}
                      >
                        {categoryGroup.category}
                      </h2>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {categoryGroup.items.map((integration, index) => (
                          <div
                            key={integration.name}
                            className={`transition-all duration-700 ${
                              categoriesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                            }`}
                            style={{transitionDelay: `${(categoryIndex * 0.1) + 0.2 + (index * 0.05)}s`}}
                          >
                            <IntegrationCard integration={integration} />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <p className="text-lg text-textSecondary dark:text-[#858585]">
                      No integrations found matching your search.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-lg text-textSecondary dark:text-[#858585]">
                  No integrations found matching your search.
                </p>
              </div>
            )}
          </div>
        </Container>
      </Section>

      {/* CTA Section */}
      <Section padding="lg" background="gray" ref={ctaRef as React.RefObject<HTMLElement>} className="bg-backgroundSecondary dark:bg-[#252526]">
        <Container>
          <div className={`max-w-3xl mx-auto text-center transition-all duration-700 ${
            ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-3xl sm:text-4xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-4">
              Don't See Your Integration?
            </h2>
            <p className="text-lg text-textSecondary dark:text-[#858585] mb-8 max-w-2xl mx-auto leading-relaxed">
              We're constantly adding new integrations. Request an integration or build your own using our API.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={ROUTES.CONTACT}>
                <Button variant="primary" size="lg" className="bg-[#1E1E1E] dark:bg-[#1E1E1E] hover:bg-[#2D2D30] dark:hover:bg-[#2D2D30] text-white">
                  Request Integration
                </Button>
              </Link>
              <a href="/docs/api" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="lg">
                  View API Docs
                </Button>
              </a>
            </div>
          </div>
        </Container>
      </Section>
    </LandingLayout>
  );
}

// Integration Card Component (without emojis)
interface IntegrationCardProps {
  integration: Integration;
}

function IntegrationCard({integration}: IntegrationCardProps) {
  // Generate a simple text-based logo from the first letter(s) of the name
  const getLogoText = () => {
    const words = integration.name.split(' ');
    if (words.length > 1) {
      return words.map((w) => w[0]).join('').toUpperCase().slice(0, 2);
    }
    return integration.name.slice(0, 2).toUpperCase();
  };

  return (
    <Card hover className="p-6 h-full">
      <div className="flex flex-col h-full">
        {/* Logo Container */}
        <div className="mb-4">
          <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-[#2D2D30] flex items-center justify-center border border-gray-200 dark:border-gray-700">
            <span className="text-lg font-bold text-textPrimary dark:text-[#CCCCCC]">
              {getLogoText()}
            </span>
          </div>
        </div>

        {/* Name and Status */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-semibold text-lg text-textPrimary dark:text-[#CCCCCC]">
              {integration.name}
            </h3>
            {integration.status === 'beta' && (
              <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded">
                Beta
              </span>
            )}
            {integration.status === 'coming-soon' && (
              <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 dark:bg-[#2D2D30] text-gray-600 dark:text-[#858585] rounded">
                Coming Soon
              </span>
            )}
          </div>
          <p className="text-sm text-textSecondary dark:text-[#858585] leading-relaxed">
            {integration.description || 'Integration with ' + integration.name}
          </p>
        </div>

        {/* Action Button */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          {integration.status === 'available' ? (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => {
                // TODO: Link to integration setup page
                window.open('#', '_blank');
              }}
            >
              Connect
            </Button>
          ) : integration.status === 'beta' ? (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              disabled
            >
              Try Beta
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              disabled
            >
              Coming Soon
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
